import { useState } from "react";
import {
    Container,
    Box,
    Typography,
    TextField,
    LinearProgress,
    useTheme,
    Chip,
    Link,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { FiSearch, FiExternalLink } from "react-icons/fi";

const Orders = ({ data = [] }) => {
    const theme = useTheme();
    const [searchText, setSearchText] = useState("");
    const [pageSize, setPageSize] = useState(10);

    const getStatusColor = (status) => {
        const statusColors = {
            active: "success",
            pending: "warning",
            complete: "info",
            new_request: "primary",
            cancelled: "error",
        };
        return statusColors[status] || "default";
    };

    const getPaymentStatusColor = (status) => {
        const statusColors = {
            paid: "success",
            pending: "warning",
        };
        return statusColors[status] || "default";
    };

    // Safe date formatter function for orders (date-only format)
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        try {
            // Parse the date string - could be "2025-11-20" or ISO format
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return "Invalid Date";
            }

            // Format as date only for orders
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return "Invalid Date";
        }
    };

    const columns: GridColDef[] = [
        {
            field: "uuid",
            headerName: "Order ID",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {params.row.uuid}
                </Box>
            ),
        },
        {
            field: "panel_url",
            headerName: "Panel URL",
            flex: 1,
            minWidth: 150,
            renderCell: (params) =>
                params.row.panel_url ? (
                    <Link
                        href={params.row.panel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <FiExternalLink size={14} />
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {params.row.panel_url.length > 30
                                ? `${params.row.panel_url.substring(0, 30)}...`
                                : params.row.panel_url
                            }
                        </Typography>
                    </Link>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Not provided
                    </Typography>
                ),
        },
        {
            field: "price",
            headerName: "Price",
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    ${params.row.price}
                </Typography>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.row.status}
                    color={getStatusColor(params.row.status)}
                    size="small"
                />
            ),
        },
        {
            field: "status_payment",
            headerName: "Payment",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.row.status_payment || 'N/A'}
                    color={getPaymentStatusColor(params.row.status_payment)}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: "payment_method",
            headerName: "Method",
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.payment_method || 'N/A'}
                </Typography>
            ),
        },
        {
            field: "created_at",
            headerName: "Created",
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2">
                    {formatDate(params.row.created_at)}
                </Typography>
            ),
        },
        {
            field: "count",
            headerName: "Count",
            width: 80,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.count || '-'}
                </Typography>
            ),
        },
    ];

    const filteredRows = data.filter((row) =>
        Object.values(row).some((value) =>
            String(value).toLowerCase().includes(searchText.toLowerCase())
        )
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box
                sx={{
                    mb: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    User Orders ({data.length})
                </Typography>

                <TextField
                    variant="outlined"
                    placeholder="Search orders..."
                    InputProps={{
                        startAdornment: <FiSearch style={{ marginRight: 8 }} />,
                    }}
                    sx={{
                        width: 300,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                        },
                    }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Box>

            <Box
                sx={{
                    height: 600,
                    width: "100%",
                    "& .MuiDataGrid-root": {
                        border: "none",
                        borderRadius: 2,
                        boxShadow: theme.shadows[2],
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: theme.palette.background.default,
                    },
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 25, 50]}
                    pagination
                    autoHeight={false}
                    disableSelectionOnClick
                    loading={false}
                    getRowId={(row) => row.request_id}
                    components={{
                        LoadingOverlay: LinearProgress,
                    }}
                    sx={{
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                />
            </Box>
        </Container>
    );
};

export default Orders;